import { colors, DATE_FORMAT, Icon, Tag, Tooltip } from '@dev-spendesk/grapes';
import classNames from 'classnames';

import { useCompany } from 'modules/app/hooks/useCompany';
import { getFailedTransactionTranslationKey } from 'modules/cards/models/failedTransaction';
import { type FailedTransaction } from 'modules/payments/components/all/failedTransactionType';
import {
  type Payment,
  type PaymentBase,
} from 'modules/payments/components/all/paymentType';
import {
  getTagDataFromPayment,
  getPaymentFormattedAmounts,
} from 'modules/payments/utils/paymentUtils';
import { UserAvatar } from 'modules/users/components';
import { EllipsisTooltip } from 'src/core/common/components/EllipsisTooltip';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import {
  getExpenditureAvatar,
  getExpenditureType,
} from 'src/core/utils/entities/expenditure';
import { metersToDistanceUnit } from 'src/core/utils/geo';
import { formatNumberToDistance } from 'src/core/utils/number';

export const AmountCell = ({ payment }: { payment: PaymentBase }) => {
  const company = useCompany();

  const { amountInCompanyCurrency, amountInExpenditureCurrency } =
    getPaymentFormattedAmounts(
      {
        amount_declared: Number.parseFloat(`${payment.amount_declared}`),
        amount_billed: Number.parseFloat(`${payment.amount_billed}`),
        fx_fee_amount: Number.parseFloat(`${payment.fx_fee_amount}`),
        currency_declared: payment.currency_declared,
      },
      company.currency,
    );

  return (
    <span
      className={classNames('flex flex-col', {
        'text-content-secondary-bg-primary': payment.state === 'authorised',
        'text-content-alert-default line-through': payment.state === 'failed',
      })}
    >
      {amountInCompanyCurrency}
      {amountInExpenditureCurrency && (
        <span
          className={classNames({
            'text-content-secondary-bg-primary': payment.state !== 'failed',
            'text-content-alert-default line-through': payment.state === 'failed',
          })}
        >
          {amountInExpenditureCurrency}
        </span>
      )}
    </span>
  );
};

export const DateCell = ({ payment }: { payment: PaymentBase }) => {
  const { localeFormat } = useTranslation('global');

  return (
    <span
      className={`block text-right ${
        payment.state === 'authorised' ? 'text-content-secondary-bg-primary' : ''
      }`}
    >
      {localeFormat(
        new Date(payment.paid_at || payment.created_at),
        DATE_FORMAT.CUSTOM,
        { dateStyle: 'medium', timeZone: 'UTC' },
      )}
    </span>
  );
};

const DescriptionCell = ({ payment }: { payment: Payment }) => {
  const company = useCompany();
  const hasTeamsFeature = useFeature(FEATURES.TEAMS);

  const { t } = useTranslation('global');

  const { description, is_atm_withdrawal: isWithdrawal, transaction } = payment;

  const paymentDescription =
    description || (transaction ? transaction.clean_description : '');

  const requestType = payment.request?.type;

  let origin;
  if (requestType === 'mileage_allowance') {
    const distanceMeters =
      payment.request?.mileage_allowance_request?.distance ?? 0;
    const distanceUnit = company.mileage_scheme?.distanceUnit ?? 'km';
    origin = (
      <span>
        {formatNumberToDistance({
          number: metersToDistanceUnit({
            distanceMeters,
            distanceUnit,
          }),
          distanceUnit,
        })}
      </span>
    );
  } else if (requestType === 'per_diem_allowance') {
    /* TODO: @CORE-5111 add number of trip days, impossible for now with fetch from grahql v1 */
    origin = null;
  } else if (isWithdrawal) {
    origin = t('misc.atmWithdrawal');
  }

  return (
    <span>
      {origin && (
        <>
          {origin}
          <span className="mx-4">•</span>
        </>
      )}
      {paymentDescription}
      {hasTeamsFeature && payment.team && (
        <>
          <span className="mx-4">•</span>
          {payment.team.name}
        </>
      )}
    </span>
  );
};

const PendingPaymentStatus = ({ payment }: { payment: Payment }) => {
  const { t } = useTranslation('global');

  if (payment.state !== 'authorised') {
    return;
  }

  let tooltipMessage = t('payments.awaitingMerchantConfirmation');
  if (
    payment.request &&
    ['expense', 'invoice'].includes(payment.request.type)
  ) {
    tooltipMessage = t('payments.awaitingWireTransfer');
  }

  return (
    <Tooltip content={tooltipMessage} placement="right" triggerAsChild>
      <Icon
        className="pending-payment-clock shrink-0"
        name="clock-outline"
        color={colors.contentDecorativeIcon}
      />
    </Tooltip>
  );
};

export const DescriptionWithPaymentIconCell = ({
  payment,
}: {
  payment: PaymentBase;
}) => {
  const { t } = useTranslation('global');
  const { icon, color, type } = getExpenditureType(
    {
      type: payment.request?.type,
      cardId: payment.card_id,
      isAtmWithdrawal: payment.is_atm_withdrawal,
    },
    t,
  );

  return (
    <div
      className={`flex items-center gap-8 ${
        payment.state === 'authorised' ? 'text-content-secondary-bg-primary' : ''
      }`}
    >
      <Tooltip content={type} triggerAsChild>
        <Icon
          name={icon}
          color={color}
          className={classNames(
            payment.state === 'failed' ? 'text-content-alert-default' : '',
            'shrink-0',
          )}
        />
      </Tooltip>
      {payment.state === 'failed' ? (
        <div className="text-content-alert-default">
          {t(
            getFailedTransactionTranslationKey(
              (payment as FailedTransaction).reason_code,
            ),
          )}
        </div>
      ) : (
        <EllipsisTooltip
          text={<DescriptionCell payment={payment as Payment} />}
        />
      )}
      <PendingPaymentStatus payment={payment as Payment} />
    </div>
  );
};

export const StatusCell = ({ payment }: { payment: PaymentBase }) => {
  const { t } = useTranslation('global');
  if (payment.state === 'failed') {
    return (
      <Tag iconName="circle-strikeout" variant="neutral">
        {t('misc.na')}
      </Tag>
    );
  }

  const { icon, variant, content } = getTagDataFromPayment(payment, t);

  return (
    <Tag iconName={icon} variant={variant}>
      {content}
    </Tag>
  );
};

export const SupplierCell = ({ payment }: { payment: PaymentBase }) => {
  const { t } = useTranslation('global');

  const { avatar, avatarTitle } = getExpenditureAvatar(
    {
      type: payment.request?.type,
      supplier: {
        id: payment.supplier_id || payment.supplier?.id,
        name: payment.supplier?.name ?? '',
      },
      size: 32,
    },
    t,
  );

  return (
    <span
      className={`flex items-center gap-8 ${
        payment.state === 'authorised' ? 'text-content-secondary-bg-primary' : ''
      }`}
    >
      {avatar}
      <EllipsisTooltip text={avatarTitle} />
    </span>
  );
};

export const EmployeeCell = ({ payment }: { payment: PaymentBase }) => {
  const { t } = useTranslation('global');

  return (
    <span
      className={`flex items-center gap-8 ${
        payment.state === 'authorised' ? 'text-content-secondary-bg-primary' : ''
      }`}
    >
      <UserAvatar
        id={payment.user._id ?? ''}
        name={payment.user?.full_name ?? ''}
        avatar={payment.user?.avatar ?? ''}
      />
      <div className="w-[calc(100%-40px)]">
        <EllipsisTooltip text={payment.user?.full_name ?? t('misc.na')} />
        {payment.costCenter && (
          <EllipsisTooltip
            text={payment.costCenter.name}
            className="text-content-secondary-bg-primary"
          />
        )}
      </div>
    </span>
  );
};
